import React, { useState, useEffect } from 'react';
import { Shield, Sword, Scroll, User, Zap, ChevronRight, Menu, X, AlertTriangle } from 'lucide-react';

const HearthcrawlLanding = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const primaryAmber = '#dfb350';
  const darkNav = 'rgba(10, 12, 16, 0.95)';

  return (
    <div className="min-h-screen text-slate-300 font-sans selection:bg-amber-500 selection:text-black overflow-x-hidden bg-[#0A0C10]">
      
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'py-2 shadow-2xl backdrop-blur-md border-b border-amber-900/30' : 'py-6'} px-6`}
           style={{ backgroundColor: isScrolled ? darkNav : 'transparent' }}>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Sword className="text-amber-500" size={28} />
            <span className="text-2xl font-black tracking-widest text-[#F4E4C4] font-serif uppercase" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
              HEARTHCRAWL
            </span>
          </div>
          
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8 font-semibold tracking-wider text-sm">
            <a href="#about" className="hover:text-amber-500 transition-colors uppercase">The Game</a>
            <a href="#demo" className="hover:text-amber-500 transition-colors uppercase">Current State</a>
            <a href="#features" className="hover:text-amber-500 transition-colors uppercase">Features</a>
            <a href="#journey" className="hover:text-amber-500 transition-colors uppercase">Progression</a>
            <a href="https://discord.gg/P8XaW7kyRZ" target="_blank" rel="noopener noreferrer" className="px-6 py-2 border border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-black transition-all rounded shadow-[0_0_15px_rgba(223,179,80,0.3)] uppercase">
              Join Discord
            </a>
          </div>

          {/* Mobile Toggle */}
          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-[#0d1117] border-b border-amber-500/20 py-4 flex flex-col items-center gap-4 shadow-xl">
            <a href="#about" className="hover:text-amber-500 transition-colors uppercase py-2">The Game</a>
            <a href="#demo" className="hover:text-amber-500 transition-colors uppercase py-2">Current State</a>
            <a href="#features" className="hover:text-amber-500 transition-colors uppercase py-2">Features</a>
            <a href="#journey" className="hover:text-amber-500 transition-colors uppercase py-2">Progression</a>
            <a href="https://discord.gg/P8XaW7kyRZ" target="_blank" rel="noopener noreferrer" className="px-8 py-3 bg-amber-500 text-black font-bold uppercase rounded mt-2 w-11/12">
              Join Discord
            </a>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <header className="relative min-h-[90vh] flex flex-col justify-center items-center text-center px-4 pt-20">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0C10]/80 to-[#0A0C10] z-10" />
          <div className="absolute inset-0 bg-black/50 z-10" />
          <img 
            src="https://img.itch.zone/aW1nLzI2ODY3NTU0LnBuZw==/original/m25umZ.png"
            alt="Hero Background"
            className="w-full h-full object-cover object-center relative mix-blend-overlay"
          />
        </div>

        <div className="relative z-20 max-w-4xl mx-auto flex flex-col items-center">
          <div className="mb-6 w-16 h-1 bg-amber-500 rounded blur-sm" />
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold mb-6 tracking-tighter uppercase font-serif text-transparent bg-clip-text bg-gradient-to-b from-[#FFF5DA] to-[#CCA350] drop-shadow-[0_4px_20px_rgba(0,0,0,0.8)]">
            Hearthcrawl
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed font-light drop-shadow-lg">
            A real-time dungeon-crawling RPG built around progression, replayability, and player-driven identity.
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-6 w-full sm:w-auto">
            <a href="https://satyrnstudios.itch.io/hearthcrawl" target="_blank" rel="noopener noreferrer" className="px-10 py-4 font-bold text-lg rounded bg-gradient-to-r from-amber-600 to-amber-500 text-black shadow-[0_0_30px_rgba(223,179,80,0.4)] transition-all hover:scale-105 hover:shadow-[0_0_40px_rgba(223,179,80,0.6)] flex items-center justify-center gap-2 group uppercase tracking-wider">
              Our Itch.io Page
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a href="https://discord.gg/P8XaW7kyRZ" target="_blank" rel="noopener noreferrer" className="px-10 py-4 font-bold text-lg rounded bg-gradient-to-r from-amber-600 to-amber-500 text-black shadow-[0_0_30px_rgba(223,179,80,0.4)] transition-all hover:scale-105 hover:shadow-[0_0_40px_rgba(223,179,80,0.6)] flex items-center justify-center gap-2 group uppercase tracking-wider">
              Join Our Discord
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
        
        <div className="absolute bottom-10 z-20 animate-bounce flex flex-col items-center opacity-70">
          <span className="text-[10px] uppercase tracking-[0.3em] mb-2 text-amber-500">Learn More</span>
          <div className="w-px h-12 bg-gradient-to-b from-amber-500 to-transparent" />
        </div>
      </header>

      {/* Divider graphic */}
      <div className="w-full h-1 bg-gradient-to-r from-transparent via-amber-800/50 to-transparent" />

      {/* Story Overview */}
      <section id="about" className="py-24 px-6 bg-[#0E1117] border-b border-amber-900/20">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <p className="text-xl text-slate-300 font-light leading-relaxed">
            You begin as a low-level worker taking on small dungeon jobs that no one else wants. These early runs are simple. Clear enemies, collect loot, and get stronger. At the same time, they introduce the core loop that defines the entire game.
          </p>
          <p className="text-xl text-amber-100 font-light leading-relaxed">
            As you continue, the game expands. Dungeons become more complex, contracts become more meaningful, and your decisions begin to shape how you approach each run.
          </p>
        </div>
      </section>

      {/* Current State / Demo Info */}
      <section id="demo" className="py-24 px-6 relative overflow-hidden bg-[#0A0C10]">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-900/10 via-[#0A0C10] to-[#0A0C10] pointer-events-none" />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="flex flex-col md:flex-row gap-12 items-center">
            
            <div className="flex-1 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm font-bold uppercase tracking-wider mb-2">
                <AlertTriangle size={16} /> Prototype Build
              </div>
              <h3 className="text-4xl font-serif font-bold text-white">Current State</h3>
              <p className="text-slate-400 font-light leading-relaxed">
                This game is still a prototype. Demos, early access, and release are still a long way away. There is no working build yet. We are completing Phase 1 core systems.
              </p>

              <div className="grid sm:grid-cols-2 gap-6 mt-8">
                <div className="bg-[#161B22] p-6 rounded border border-slate-800">
                  <h4 className="text-amber-500 font-bold mb-3 uppercase tracking-wider text-sm flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Demo Includes</h4>
                  <ul className="space-y-2 text-slate-300 text-sm">
                    <li>• Foundational dungeon gameplay</li>
                    <li>• Early contract systems</li>
                    <li>• Initial progression mechanics</li>
                    <li>• 1 dungeon & 2-4 delves</li>
                    <li>• 1-3 taverns & central hub</li>
                  </ul>
                </div>
                <div className="bg-[#161B22] p-6 rounded border border-slate-800 opacity-70">
                  <h4 className="text-slate-500 font-bold mb-3 uppercase tracking-wider text-sm flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-slate-500" /> Not Yet Included</h4>
                  <ul className="space-y-2 text-slate-400 text-sm">
                    <li>• Reputation system</li>
                    <li>• Changing dungeons based on reputation</li>
                    <li>• Fully featured game content</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex-1 bg-[#161B22] border border-slate-800 rounded-xl p-8 shadow-2xl relative">
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <Sword size={120} />
              </div>
              <h4 className="text-2xl font-serif text-white mb-4">Support & Feedback</h4>
              <p className="text-slate-400 mb-6 font-light leading-relaxed">
                Feedback at this stage will be valuable and directly influence development. If you play, consider sharing bugs, balance concerns, and gameplay suggestions.
              </p>
              <div className="border-l-2 border-amber-500 pl-4 py-2">
                <p className="text-amber-100 text-sm italic">
                  "This game is currently free to play during development. If you choose to support in any amount ($1 or more), you will receive a Steam key at launch as a thank-you."
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Core Features / Mechanics Section */}
      <section id="features" className="relative py-24 px-6 bg-[#0E1117] border-y border-amber-900/20">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-sm font-bold tracking-[0.2em] text-amber-500 uppercase mb-3">Gameplay</h2>
            <h3 className="text-4xl md:text-5xl font-serif font-bold text-white">Core Features</h3>
            <div className="mt-6 w-24 h-1 bg-amber-500 mx-auto rounded opacity-50" />
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            
            <div className="group p-8 bg-[#161B22] border border-slate-800 rounded hover:border-amber-600/30 transition-all duration-300">
              <Sword size={32} className="text-amber-500 mb-6" />
              <h4 className="text-2xl font-serif font-bold text-white mb-4">Dungeon-Driven Gameplay</h4>
              <p className="text-slate-400 font-light mb-4">
                The game is built around structured dungeon runs. Each dungeon has a specific purpose, enemy set, and reward structure.
              </p>
              <ul className="text-sm text-slate-500 space-y-2">
                <li>• Fully clear dungeons for maximum rewards</li>
                <li>• Target specific objectives for efficiency</li>
                <li>• Move quickly through high-risk areas</li>
              </ul>
            </div>

            <div className="group p-8 bg-[#161B22] border border-slate-800 rounded hover:border-amber-600/30 transition-all duration-300">
              <Scroll size={32} className="text-amber-500 mb-6" />
              <h4 className="text-2xl font-serif font-bold text-white mb-4">Contract System (Bounty Board)</h4>
              <p className="text-slate-400 font-light mb-4">
                Contracts determine why you enter a dungeon, creating different playstyles within the same content.
              </p>
              <ul className="text-sm text-slate-500 space-y-2">
                <li>• Stabilize threats by clearing dangerous areas</li>
                <li>• Eliminate key targets within a dungeon</li>
                <li>• Retrieve items or take on higher-risk tasks for greater rewards</li>
              </ul>
            </div>

            <div className="group p-8 bg-[#161B22] border border-slate-800 rounded hover:border-amber-600/30 transition-all duration-300">
              <Shield size={32} className="text-amber-500 mb-6" />
              <h4 className="text-2xl font-serif font-bold text-white mb-4">Real-Time Combat</h4>
              <p className="text-slate-400 font-light mb-4">
                Combat is fully real-time and movement-based. You are not forced into encounters.
              </p>
              <ul className="text-sm text-slate-500 space-y-2">
                <li>• Engage enemies directly</li>
                <li>• Avoid unnecessary fights</li>
                <li>• Reposition and control the pace of combat</li>
              </ul>
            </div>

            <div className="group p-8 bg-[#161B22] border border-slate-800 rounded hover:border-amber-600/30 transition-all duration-300 relative overflow-hidden">
              <div className="absolute right-0 bottom-0 opacity-5 w-32 h-32 bg-amber-500 rounded-full blur-3xl" />
              <User size={32} className="text-amber-500 mb-6 relative z-10" />
              <h4 className="text-2xl font-serif font-bold text-white mb-4 relative z-10">Reputation (Legacy) System</h4>
              <p className="text-slate-400 font-light mb-4 relative z-10">
                Your actions build a hidden reputation over time. You are not locked into a predefined role.
              </p>
              <div className="space-y-3 mt-4 text-sm relative z-10">
                <div className="flex gap-3"><span className="text-amber-500 font-bold w-16">Hero</span> <span className="text-slate-400">protect and stabilize the world</span></div>
                <div className="flex gap-3"><span className="text-slate-500 font-bold w-16">Neutral</span> <span className="text-slate-400">work efficiently for profit</span></div>
                <div className="flex gap-3"><span className="text-red-500 font-bold w-16">Villain</span> <span className="text-slate-400">exploit dangerous systems for rewards</span></div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Progression Timeline */}
      <section id="journey" className="py-24 px-6 bg-gradient-to-b from-[#0A0C10] to-[#12151C] relative">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-sm font-bold tracking-[0.2em] text-cyan-500 uppercase mb-3">Evolution</h2>
            <h3 className="text-4xl md:text-5xl font-serif font-bold text-white">The Character Journey</h3>
            <p className="max-w-2xl mx-auto mt-4 text-slate-400">Each character represents a separate journey with no shared progression. Each run is defined entirely by your decisions.</p>
          </div>

          <div className="relative border-l-2 border-slate-800 ml-4 md:ml-1/2 space-y-16 pl-8 py-4">
            
            {/* Timeline Item 1 */}
            <div className="relative">
              <div className="absolute -left-[41px] top-2 bg-slate-900 border-2 border-cyan-500 w-5 h-5 rounded-full z-10 shadow-[0_0_10px_rgba(6,182,212,0.5)]" />
              <div className="bg-[#161B22] p-8 rounded-xl border border-slate-800 hover:border-cyan-900/50 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-cyan-900/30 rounded text-cyan-400"><Zap size={24} /></div>
                  <div>
                    <span className="text-xs font-bold text-cyan-500 uppercase tracking-widest">Phase One</span>
                    <h4 className="text-2xl font-serif font-bold text-white">Leveling (1–70)</h4>
                  </div>
                </div>
                <p className="text-slate-400 font-light mb-6">Build your character, learn systems, and shape your identity through gameplay.</p>
              </div>
            </div>

            {/* Timeline Item 2 */}
            <div className="relative">
              <div className="absolute -left-[41px] top-2 bg-slate-900 border-2 border-amber-500 w-5 h-5 rounded-full z-10 shadow-[0_0_10px_rgba(245,158,11,0.5)]" />
              <div className="bg-[#161B22] p-8 rounded-xl border border-slate-800 hover:border-amber-900/50 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-amber-900/30 rounded text-amber-400"><Shield size={24} /></div>
                  <div>
                    <span className="text-xs font-bold text-amber-500 uppercase tracking-widest">Phase Two</span>
                    <h4 className="text-2xl font-serif font-bold text-white">Dungeon Power (DP)</h4>
                  </div>
                </div>
                <p className="text-slate-400 font-light">Unlocked at max level. This phase focuses on long-term progression. Your identity is locked based on earlier choices, progression slows, and focus shifts to optimizing builds and pushing content.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Footer / Call to Action */}
      <footer className="relative bg-[#050608] pt-20 pb-10 border-t border-slate-800 overflow-hidden">
        {/* Subtle background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-px shadow-[0_0_40px_10px_rgba(223,179,80,0.15)]" />
        
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 lg:grid-cols-3 gap-12 mb-16 relative z-10 text-center md:text-left">
          <div className="col-span-1 lg:col-span-2">
            <div className="flex items-center justify-center md:justify-start gap-2 mb-6">
              <Sword className="text-amber-500" size={24} />
              <span className="text-2xl font-black tracking-widest text-[#F4E4C4] font-serif uppercase">HEARTHCRAWL</span>
            </div>
            <p className="text-slate-500 font-light max-w-md mb-6 mx-auto md:mx-0">
              An evolving dark fantasy RPG. Support development, track patch notes, and join a growing community of delvers.
            </p>
            <a href="https://discord.gg/P8XaW7kyRZ" target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-amber-600 text-black font-bold uppercase tracking-wider rounded text-sm hover:bg-amber-500 transition-colors inline-flex items-center gap-2">
              <Menu size={16} /> Join Discord Community
            </a>
          </div>

          <div>
            <h4 className="text-white font-bold tracking-wide uppercase mb-4">Development Notes</h4>
            <ul className="space-y-3 font-light text-sm text-slate-500">
              <li>• Systems and balance will change</li>
              <li>• Currently working on Phase 1 core systems</li>
            </ul>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 text-center text-slate-600 text-xs uppercase tracking-widest font-bold">
          <p>© {new Date().getFullYear()} SatyrnStudios. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default HearthcrawlLanding;